import { createRouter, createWebHashHistory, NavigationGuardNext, RouteLocationNormalizedGeneric, RouteRecordNameGeneric } from "vue-router";
import { redirectToLogin } from "./handlerAuthentication";
import { canUserAccess, isUserFirstLogin } from "./helpers/authenticateRoute";
import notificationsRoute from "./notifications.route";
import handleBreadcrumb from "./handleBreadcrumb";
import settingsRoutes from "./settings.route";
import governmentServiceRoute from "./governmentServices.route";
import { eGuaranteeTypes } from "@ntprsrf-frontend/internet/src/constants/APIEnums";
import { corppassLogin } from "@ntprsrf-frontend/common/src/utils/corppass";
import useAuthStore from "../stores/auth.store";
import useCompanyStore from "@ntprsrf-frontend/internet/src/stores/company.store";
import { useBreadcrumbStore } from "@/stores/breadcrumbv2.store";

const currEnv = import.meta.env.VITE_ENVIRONMENT ?? "prd";

const eGuaranteeAccessTypes = [
  eGuaranteeTypes.NORMAL.VALUE,
  eGuaranteeTypes.FINANCIAL_INSTITUTION.VALUE,
  eGuaranteeTypes.GOVERNMENT_AGENCY.VALUE,
];

const createRoute = (
  path: string,
  name: string,
  label: string,
  ignoresAuth: boolean,
  component: () => Promise<any>,
  extra: Record<string, any> = {}
) => {
  const { meta: extraMeta, ...otherExtra } = extra;
  return {
    path,
    name,
    component,
    meta: {
      label,
      ignoresAuth,
      eGuaranteeTypesCanAccess: eGuaranteeAccessTypes,
      ...extraMeta,
    },
    ...otherExtra,
  };
};

const routeMetaData: Array<[string, string, string, boolean, () => Promise<any>]> = [
  ["/redirect", "REDIRECT", "Redirect", true, () => import("@/views/Redirect.vue")],
  ["/login", "LOGIN", "login", true, () => import("@/views/Login.vue")],
  ["/login-company", "LOGIN_COMPANY", "login-company", true, () => import("@/views/LoginCompany.vue")],
  ["/login-test/:query?", "LOGIN_TEST", "LoginTest", true, () => import("@/views/LoginTest.vue")],
  ["/login-redirect/:query?", "LOGIN_REDIRECT", "LoginRedirect", true, () => import("@/views/LoginTestRedirect.vue")],
  ["/400", "400_REDIRECTERROR", "400 Redirect Error", true, () => import("@/views/400_RedirectError.vue")],
  ["/corppass", "CORPPASS", "Corppass Login", true, () => import("@/views/Corppass.vue")],
];

const detailRouteMeta: Array<[string, string, string, boolean, () => Promise<any>]> = [
  ["privacy-statement", "PRIVACY_STATEMENT", "Privacy Statement", true, () => import("@/views/PrivacyStatement.vue")],
  ["terms-of-use", "TERMS_OF_USE", "Terms of Use", true, () => import("@/views/TermsOfUse.vue")],
  ["contact-us", "CONTACT_US", "Contact Us", true, () => import("@/views/ContactUs.vue")],
  ["about-us", "ABOUT_US", "About Us", true, () => import("@/views/AboutUs.vue")],
  ["get-started", "GET_STARTED", "Get Started", true, () => import("@/views/GetStarted.vue")],
];

const homeRouteMeta: Array<[string, string, string, boolean, () => Promise<any>, Record<string, any> | null]> = [
  ["dashboard", "DASHBOARD", "Dashboard", false, () => import("@/views/Dashboard.vue"), null],
  ["/", "HOME PAGE", "HOME PAGE", true, () => import("@/views/Homepage.vue"), { rebrand: true }],
  ["timeout", "TIMEOUT", "Timeout", true, () => import("@/views/SessionTimeout.vue"), { rebrand: true }],
  ["services", "SERVICES", "Services", false, () => import("@/views/ServiceSubscriptions/Services.vue"), null],
];

const rebrandErrorRouteMeta: Array<[string, string, string, boolean, () => Promise<any>]> = [
  ["403", "REBRAND_403", "403", true, () => import("@/views/ErrorPage.vue")],
  ["404", "REBRAND_404", "404", true, () => import("@/views/ErrorPage.vue")],
  ["503", "REBRAND_503", "503", true, () => import("@/views/ErrorPage.vue")],
  ["/:pathMatch(.*)*", "REBRAND_404", "404", true, () => import("@/views/ErrorPage.vue")],
  ["maintenance", "MAINTENANCE", "Maintenance", true, () => import("@/views/Maintenance.vue")],
];

const routesRef = routeMetaData.map(([path, name, label, ignoresAuth, component]) =>
  createRoute(path, name, label, ignoresAuth, component)
);

const detailRoutes = detailRouteMeta.map(([path, name, label, ignoresAuth, component]) =>
  createRoute(path, name, label, ignoresAuth, component)
);

const homeRoutes = homeRouteMeta.map(([path, name, label, ignoresAuth, component, meta]) =>
  createRoute(path, name, label, ignoresAuth, component, meta ? { meta } : {})
);

const rebrandErrorRoutes = rebrandErrorRouteMeta.map(([path, name, label, ignoresAuth, component]) =>
  createRoute(path, name, label, ignoresAuth, component, { meta: { rebrand: true } })
);

const updateProfileRoute = createRoute(
  "update-profile",
  "STARTING_FORM",
  "Update Profile",
  false,
  () => import("@/views/PreStartingForm.vue"),
  {
    meta: { 
      rebrand: true,
      breadcrumb: () => {
        const username = useAuthStore().username?.trim();
        const welcomeLabel = username ? `Welcome ${username}!` : "Welcome!";

        return {
          label: welcomeLabel,
          title: welcomeLabel,
        };
      },
      hideBreadcrumbBlock: true,
    },
    beforeEnter: (
      _: RouteLocationNormalizedGeneric,
      _2: RouteLocationNormalizedGeneric,
      next: NavigationGuardNext
    ) => {
      const authStore = useAuthStore();
      if (!authStore.hasAgreedToTNC) {
        next({ name: "TERMS_AND_CONDITIONS" });
      } else next();
    },
  }
);


const detailWrapper = createRoute("", "DETAIL", "", false, () => import("@/views/_ViewWithBreadcrumb.vue"), {
  children: [
    ...detailRoutes,
  ],
});

const rebrandDetailWrapperMain = createRoute("", "REBRAND_DETAIL_MAIN", "Home", false, () => import("@/views/_ViewWithBreadcrumb.vue"), {
  children: [
    ...governmentServiceRoute,
    ...settingsRoutes,
    ...notificationsRoute,
    {
      path: "dashboard",
      name: "DASHBOARD",
      component: () => import("@/views/Dashboard.vue"),
      meta: {
        label: "My eServices",
        rebrand: true,
      },
    },
    {
      path: "my-profile",
      name: "USER_PROFILE",
      component: () => import("@/views/UserProfile.vue"),
      meta: {
        label: "My Profile",
        rebrand: true,
        eGuaranteeTypesCanAccess: eGuaranteeAccessTypes,
      },
    },
    {
      path: "sitemap",
      name: "SITEMAP",
      component: () => import("@/views/Sitemap.vue"),
      meta: {
        label: "Sitemap",
        rebrand: true,
        ignoresAuth: true,
        eGuaranteeTypesCanAccess: eGuaranteeAccessTypes,
      },
    },
    updateProfileRoute,
    {
      path: "terms-and-conditions",
      name: "TERMS_AND_CONDITIONS",
      meta: {
        rebrand: true,
        ignoresAuth: true,
        label: "Terms and Conditions",
      },
      component: () => import("@/views/TermsAndConditions.vue"),
    },
  ],
});

export const routes = [
  ...routesRef,
  {
    path: "/",
    name: "HOME",
    component: () => import("@/views/Home.vue"),
    redirect: () => {
      if(
        !useAuthStore().isLogin
      ) {
        return {
          name: "HOME PAGE",
        };
      }
      
      return {
        name: "DASHBOARD",
      };
    },
    meta: {
      isInformationalService: true,
      eGuaranteeTypesCanAccess: eGuaranteeAccessTypes,
    },
    children: [
      ...homeRoutes,
      ...rebrandErrorRoutes,
      rebrandDetailWrapperMain,
      // To be deleted when all home routes are rebranded
      {
        path: "form",
        name: "FORM_APPLICATIONS",
        meta: {
          label: "Form Applications",
        },
        redirect: {
          name: "MAINTENANCE",
        },
      },
      ...(currEnv !== 'prd' ? [
        {
          path: "routes-list",
          name: "ROUTES_LIST",
          component: () => import("@/views/RouteList.vue"),
        }
      ] : []),
    ],
  },
];
export const router = createRouter({
  history: createWebHashHistory(),
  routes
});

const isProdAndAccessDevLogin = (name: RouteRecordNameGeneric) => currEnv === 'prd' && (name === 'LOGIN_TEST' || name === 'LOGIN_COMPANY');

type GuardRedirect = { name: string } | false | null;

const resetTimeoutAccessOnLeave = (
  from: RouteLocationNormalizedGeneric,
  to: RouteLocationNormalizedGeneric,
  setTimeoutPageAccessible: (value: boolean) => void
) => {
  if (from.name === "TIMEOUT" && to.name !== "TIMEOUT") {
    setTimeoutPageAccessible(false);
  }
};

const getEntryRedirect = (
  to: RouteLocationNormalizedGeneric,
  timeoutPageAccessible: boolean,
  isLogin: boolean
): GuardRedirect => {
  if (isProdAndAccessDevLogin(to.name)) {
    return { name: 'REBRAND_404' };
  }

  if (to.name === "TIMEOUT" && !timeoutPageAccessible) {
    return { name: "HOME PAGE" };
  }

  if ((to.name === "HOME PAGE" || to.name === "LOGIN") && isLogin) {
    return { name: "DASHBOARD" };
  }

  return null;
};

const handleLoginPageAccess = (to: RouteLocationNormalizedGeneric, isLogin: boolean): GuardRedirect => {
  if (to.name === "LOGIN" && !isLogin) {
    corppassLogin("/");
    return false;
  }

  return null;
};

const handleUnauthenticatedAccess = (to: RouteLocationNormalizedGeneric, hasAuth: boolean): GuardRedirect => {
  if (hasAuth) {
    return null;
  }

  if (to.name === "DASHBOARD") {
    return { name: "HOME PAGE" };
  }

  corppassLogin(to.fullPath);
  return false;
};

const clearMatchingRedirect = (toPath: string) => {
  const redirectString = localStorage.getItem("redirect");
  if (!redirectString) {
    return;
  }

  const redirectPage = JSON.parse(redirectString);
  if (redirectPage.path === toPath) {
    localStorage.removeItem("redirect");
  }
};

router.beforeEach(async (to, from) => {
  const ignoresAuth = to.meta.ignoresAuth === true;
  const hasAuth = !redirectToLogin(to);
  const hasAccess = canUserAccess(to);
  const isFirstLogin = isUserFirstLogin(to);
  const { isLogin, timeoutPageAccessible, setTimeoutPageAccessible } = useAuthStore();

  const breadCrumbStore = useBreadcrumbStore()

  resetTimeoutAccessOnLeave(from, to, setTimeoutPageAccessible);

  const redirect = getEntryRedirect(to, timeoutPageAccessible, isLogin);
  if (redirect !== null) {
    return redirect;
  }

  const loginPageAccessCheck = handleLoginPageAccess(to, isLogin);
  if (loginPageAccessCheck !== null) {
    return loginPageAccessCheck;
  }

  const unauthenticatedAccessCheck = handleUnauthenticatedAccess(to, hasAuth);
  if (unauthenticatedAccessCheck !== null) {
    return unauthenticatedAccessCheck;
  }

  const accessCheck = checkAccess(ignoresAuth, hasAccess);
  if (accessCheck) return accessCheck;

  if (isFirstLogin) {
    return {
      name: "TERMS_AND_CONDITIONS",
    };
  }

  if (to.path.startsWith('/government-services')) {
    return {
      path: to.path.replace('/government-services', '/eservices'),
      query: to.query,
      hash: to.hash,
      replace: true,
    };
  }

  clearMatchingRedirect(to.path);
  if(to.meta.transactionalServiceId) {
    (window as any).wogaaCustom?.startTransactionalService(to.meta.transactionalServiceId);
  }

  if (to.meta.isCompanyStore) {
    await companyStore(to);
  }

  breadCrumbStore.reset()
  handleBreadcrumb(to);
});

function checkAccess(ignoresAuth: boolean, hasAccess: boolean | undefined) {
  if (!ignoresAuth) {
    if (hasAccess === undefined) {
      return { name: "REBRAND_404" };
    }
    if (!hasAccess) {
      return { name: "REBRAND_403" };
    }
  }
  return null;
}


async function companyStore(to: RouteLocationNormalizedGeneric) {
  const store = useCompanyStore();
  const companyUEN = to.params.companyUEN ?? to.params.otherCompanyUEN;
  if (companyUEN !== store.detail?.companyUEN) {
    await store.setRouteInfo(to);
  }
}

export default router;
